import { BehaviorSubject, type Observable } from 'rxjs';

import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { ENVIRONMENT_REPOSITORY } from '@dependencies/shared';

export interface Theme {
	name: string;
}

@Injectable()
export class ThemeService {
	private environmentRepository = inject(ENVIRONMENT_REPOSITORY);
	private readonly document: Document = inject(DOCUMENT);

	private _currentTheme = new BehaviorSubject<Theme | null>(null);
	public readonly currentTheme$: Observable<Theme | null> = this._currentTheme.asObservable();

	private currentThemeLinkElement: HTMLLinkElement | null = null;

	private availableThemes: { [key: string]: Theme } = {
		neki: {
			name: 'neki'
		},
		savefamily: {
			name: 'savefamily'
		}
	};

	public initializeThemeFromEnvironment(): void {
		try {
			const themeName = this.environmentRepository.theme;

			const themeToLoad = themeName in this.availableThemes ? themeName : 'neki';
			this.changeTheme(this.getThemeByName(themeToLoad));
		} catch (error) {
			console.error('Fatal error during ThemeService initialization', error);
		}
	}

	public getAssetPath(relativePath: string): string {
		const theme = this._currentTheme.getValue()?.name || 'neki';
		return `/assets/themes/${theme}/${relativePath}`;
	}

	private updateThemeMetadata(themeName: string): void {
		const faviconPath = `/assets/themes/${themeName}/favicon/favicon.ico`;

		let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
		if (!link) {
			link = document.createElement('link');
			link.rel = 'icon';
			document.head.appendChild(link);
		}
		link.href = faviconPath;

		const titlesByTheme: Record<string, string> = {
			neki: 'Neki',
			savefamily: 'SaveFamily'
		};

		document.title = titlesByTheme[themeName] || 'Mi aplicación';
	}

	private getThemeByName(themeName: string): Theme {
		return this.availableThemes[themeName];
	}

	private getCssBundleNameByName(themeName: string): string {
		return `theme-${themeName}.css`;
	}

	public changeTheme(theme: Theme): void {
		this.applyThemeClass(theme.name);
		this.loadThemeCss(theme);
		this.updateThemeMetadata(theme.name);
		this._currentTheme.next(theme);
	}

	private applyThemeClass(themeName: string): void {
		const rootElement = this.document.documentElement;

		rootElement.classList.forEach((className) => {
			if (className.startsWith('theme-')) {
				rootElement.classList.remove(className);
			}
		});

		rootElement.classList.add(`theme-${themeName}`);
	}

	private loadThemeCss(theme: Theme): void {
		if (this.currentThemeLinkElement) {
			this.currentThemeLinkElement.remove();
			this.currentThemeLinkElement = null;
		}

		const cssBundleName = this.getCssBundleNameByName(theme.name);

		const link = this.document.createElement('link');
		link.rel = 'stylesheet';
		link.type = 'text/css';
		link.href = cssBundleName;
		link.classList.add('app-theme-style');

		link.onload = () => console.log(`Theme loaded: ${theme.name}`);
		link.onerror = () => console.error(`Failed to load theme: ${theme.name}`);

		this.document.head.appendChild(link);
		this.currentThemeLinkElement = link;
	}
}
